import axios from 'axios';

/**
 * API utilities for achievements
 */

/**
 * Fetch all available achievements from config
 * @returns {Promise<Object>} Response with achievements data
 */
export const getAllAchievements = async () => {
  try {
    const response = await axios.get('/api/achievements', {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all achievements:', error);
    throw error;
  }
};

/**
 * Fetch user's achievements with progress
 * @returns {Promise<Object>} Response with user achievements and stats
 */
export const getUserAchievements = async () => {
  try {
    const response = await axios.get('/api/users/me/achievements', {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user achievements:', error);
    throw error;
  }
};

/**
 * Calculate and unlock new achievements for the user
 * @returns {Promise<Object>} Response with newly unlocked achievements
 */
export const calculateAchievements = async () => {
  try {
    const response = await axios.post(
      '/api/users/me/achievements/calculate',
      {},
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error calculating achievements:', error);
    throw error;
  }
};

/**
 * Check achievements for a specific user (admin only)
 * @param {string} userDocumentId - User document ID
 * @returns {Promise<Object>} Response with check results
 */
export const checkUserAchievements = async (userDocumentId) => {
  try {
    const response = await axios.post(
      '/api/achievements/check',
      {
        userDocumentId,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error checking user achievements:', error);
    throw error;
  }
};

/**
 * Get achievements for a specific user (admin only)
 * @param {string} userDocumentId - User document ID
 * @returns {Promise<Object>} Response with user achievements
 */
export const getUserAchievementsById = async (userDocumentId) => {
  try {
    const response = await axios.get(
      `/api/achievements/user/${userDocumentId}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching user achievements by ID:', error);
    throw error;
  }
};

/**
 * Helper function to check if user has specific achievement unlocked
 * @param {Array} achievements - User's achievements array
 * @param {string} achievementKey - Key of the achievement to check
 * @returns {boolean} True if achievement is unlocked
 */
export const isAchievementUnlocked = (achievements, achievementKey) => {
  return achievements.some(
    (achievement) => achievement.key === achievementKey && achievement.unlocked
  );
};

/**
 * Helper function to get achievement progress
 * @param {Array} achievements - User's achievements array
 * @param {string} achievementKey - Key of the achievement to check
 * @returns {number} Progress value
 */
export const getAchievementProgress = (achievements, achievementKey) => {
  const achievement = achievements.find((a) => a.key === achievementKey);
  return achievement ? achievement.progress : 0;
};

/**
 * Helper function to get total points earned
 * @param {Array} achievements - User's achievements array
 * @returns {number} Total points from unlocked achievements
 */
export const getTotalPoints = (achievements) => {
  return achievements
    .filter((achievement) => achievement.unlocked)
    .reduce((total, achievement) => total + (achievement.points || 0), 0);
};

/**
 * Helper function to get achievements by category
 * @param {Array} achievements - User's achievements array
 * @param {string} category - Category to filter by
 * @returns {Array} Filtered achievements
 */
export const getAchievementsByCategory = (achievements, category) => {
  return achievements.filter(
    (achievement) => achievement.category === category
  );
};

/**
 * Helper function to get unlocked achievements count
 * @param {Array} achievements - User's achievements array
 * @returns {number} Count of unlocked achievements
 */
export const getUnlockedCount = (achievements) => {
  return achievements.filter((achievement) => achievement.unlocked).length;
};

/**
 * Helper function to get completion percentage
 * @param {Array} achievements - User's achievements array
 * @returns {number} Completion percentage (0-100)
 */
export const getCompletionPercentage = (achievements) => {
  if (achievements.length === 0) return 0;
  const unlocked = getUnlockedCount(achievements);
  return Math.round((unlocked / achievements.length) * 100);
};

/**
 * Helper function to get achievement statistics
 * @param {Array} achievements - User's achievements array
 * @returns {Object} Statistics object
 */
export const getAchievementStats = (achievements) => {
  const unlocked = getUnlockedCount(achievements);
  const totalPoints = getTotalPoints(achievements);
  const completionPercentage = getCompletionPercentage(achievements);

  // Group by category
  const byCategory = achievements.reduce((acc, achievement) => {
    const { category } = achievement;
    if (!acc[category]) {
      acc[category] = { total: 0, unlocked: 0, points: 0 };
    }
    acc[category].total += 1;
    if (achievement.unlocked) {
      acc[category].unlocked += 1;
      acc[category].points += achievement.points || 0;
    }
    return acc;
  }, {});

  return {
    total: achievements.length,
    unlocked,
    locked: achievements.length - unlocked,
    totalPoints,
    completionPercentage,
    byCategory,
  };
};
